import React from 'react';
import {
    Dialog, DialogTitle, DialogContent, 
    Typography, Box, IconButton, Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { formatDateDisplay, formatDateTime } from '../utils/dateUtils';
import { formatCurrency } from '../utils/currencyUtils';

const BillDetailDialog = ({ open, onClose, billData, bookingData }) => {
    if (!billData) return null;

    const {
        type, amount, note, imageUrl, supplierName, status, transaction_date, created_at, date,
        payment_account_name, receive_account_name, sender_info, receiver_info
    } = billData;

    const bookingCode = bookingData?.booking_code || '---';
    const customerName = bookingData?.customer_name || bookingData?.customer_detail?.name || bookingData?.customer?.name || '---';
    const startDate = formatDateDisplay(bookingData?.start_date);
    const endDate = formatDateDisplay(bookingData?.end_date);
    const saleOwner = bookingData?.sale_owner_name || '---';

    const isRefund = type && (type === 'REFUND' || type.includes('HOÀN TIỀN'));

    let isMoneyIn = type && [
        'THU', 'KHÁCH', 'PAYMENT', 'DEPOSIT', 'BOOKING', 'TRANSFER', 'CASH', 'COLLECTION'
    ].some(k => type.toUpperCase().includes(k));

    if (payment_account_name) {
        isMoneyIn = false;
    } else if (receive_account_name) {
        isMoneyIn = true;
    }

    const beneficiaryDisplay = receive_account_name 
        ? receive_account_name 
        : (receiver_info || supplierName || '---');

    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            maxWidth="md" 
            fullWidth
            PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }} 
        >
            {/* --- HEADER --- */}
            <DialogTitle sx={{ 
                bgcolor: 'white', 
                color: '#333', 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                borderBottom: '1px solid #e0e0e0',
                px: 3, py: 2
            }}>
                <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#007373' }}>
                    CHI TIẾT BILL - {type?.toUpperCase()}
                </Typography>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            
            {/* --- BODY: Layout Flexbox 50/50 --- */}
            <DialogContent sx={{ p: 0, m: 0, overflow: 'hidden' }}> 
                <Box sx={{ 
                    display: 'flex',        
                    width: '100%',          
                    height: '60vh',         
                    flexDirection: 'row'    
                }}> 
                    
                    {/* KHUNG TRÁI: THÔNG TIN (50%) */}
                    <Box sx={{ 
                        width: '50%',               
                        borderRight: '1px solid #eee', 
                        height: '100%', 
                        overflowY: 'auto',          
                        p: '20px 40px',
                        boxSizing: 'border-box'     
                    }}>
                        <Box sx={{ pr: 1 }}>
                            {bookingData && (
                                <>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#D67000', mb: 2 }}>
                                        Thông tin Booking
                                    </Typography>

                                    <InfoRow label="Mã Booking" value={bookingCode} />
                                    <InfoRow label="Khách hàng" value={customerName} />
                                    <InfoRow label="Lịch trình" value={`${startDate} - ${endDate}`} />
                                    <InfoRow label="NV phụ trách" value={saleOwner} />

                                    <Divider sx={{ my: 3, borderColor: '#e0e0e0', borderBottomWidth: 2 }} />
                                </>
                            )}

                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#D67000', mb: 2 }}>
                                Thông tin Giao dịch ({isMoneyIn ? 'THU TIỀN' : 'CHI TIỀN'})
                            </Typography>
                            <InfoRow label="Thời gian GD" value={formatDateTime(transaction_date || created_at || date)} />

                            {isMoneyIn ? (
                                <>
                                    <InfoRow label="Người chuyển" value={sender_info || customerName} />
                                    <InfoRow label="Thu về TK" value={receive_account_name || '---'} isBoldValue />
                                </>
                            ) : (
                                <>
                                    <InfoRow label="Chi từ TK" value={payment_account_name || '---'} isBoldValue />
                                    <InfoRow label="Người thụ hưởng" value={beneficiaryDisplay} />
                                </>
                            )}
                            
                            <InfoRow label="Số tiền" value={`${formatCurrency(amount)} VND`} isBoldValue />
                            <InfoRow label="Trạng thái" value={(status === 'PAID' || status === 'DONE') ? 'Đã thanh toán' : 'Chưa thanh toán'} />
                            <InfoRow label="Ghi chú" value={note || 'Không có'} />
                        </Box>
                    </Box>

                    {/* KHUNG PHẢI: ẢNH (50%) */}
                    <Box sx={{ 
                        width: '50%',               
                        height: '100%', 
                        bgcolor: '#f2f4f7',         
                        display: 'flex',            
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 2,
                        boxSizing: 'border-box'
                    }}>
                        <Box sx={{ 
                            width: '100%', 
                            height: '100%', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            position: 'relative'
                        }}>
                            {imageUrl ? (
                                <a href={imageUrl} target="_blank" rel="noreferrer" style={{
                                    display: 'flex', 
                                    width: '100%', 
                                    height: '100%', 
                                    alignItems: 'center', 
                                    justifyContent: 'center'
                                }}>
                                    <img 
                                        src={imageUrl} 
                                        alt="Ảnh bill" 
                                        style={{ 
                                            maxWidth: '100%', 
                                            maxHeight: '100%', 
                                            objectFit: 'contain', 
                                            borderRadius: '4px',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)' 
                                        }} 
                                    />
                                </a>
                            ) : (
                                <Typography color="text.secondary" variant="body2">
                                    Không có ảnh bill
                                </Typography>
                            )}
                        </Box>
                    </Box>

                </Box>
            </DialogContent>
        </Dialog>
    );
};

// Component InfoRow: Căn trái toàn bộ (Label | Value), không gạch chân
const InfoRow = ({ label, value, isBoldValue = false }) => (
    <Box sx={{ 
        display: 'flex', 
        alignItems: 'flex-start', // Căn hàng theo dòng trên cùng
        mb: 1.5,                  // Khoảng cách giữa các dòng
    }}>
        <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ 
                width: '130px',   // Cố định chiều rộng Label để các dòng thẳng hàng
                flexShrink: 0,    // Không bị co lại
                fontWeight: 500
            }}
        >
            {label}:
        </Typography>
        <Typography 
            variant="body2" 
            sx={{ 
                color: isBoldValue ? '#333' : '#333', 
                fontWeight: isBoldValue ? 'bold' : '400',
                textAlign: 'left', 
                flex: 1,
                wordBreak: 'break-word'
            }}
        >
            {value}
        </Typography>
    </Box>
);

export default BillDetailDialog;